/**
 * useOsUserSettings
 *
 * Manages per-user aniOS settings (pinned/hidden dock apps) with a
 * write-through cache: localStorage for instant reads, debounced API sync.
 *
 * Kept separate from `useWorkspaceConfig` because these prefs change rarely
 * and have a much smaller payload — no need for the heavier reconciliation
 * machinery used for window state.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  getOsSettings,
  saveOsSettings,
} from "@/services/api/os-settings";
import type { OsSettingsRequest } from "@/services/api/os-settings/types";
import {
  readCachedOsSettings,
  writeCachedOsSettings,
} from "@/utils/osUserSettingsStorage";
import { toOsSettingsSyncPayload } from "@/utils/osUserSettingsSync";

const SYNC_DEBOUNCE_MS = 1000;

const DEFAULT_SETTINGS: OsSettingsRequest = {
  pinnedAppIds: [],
  hiddenAppIds: [],
  reopenWindowsOnLaunch: false,
  openWindowsMaximized: false,
};

function readLocal(userId: string): OsSettingsRequest | null {
  return readCachedOsSettings(userId);
}

function writeLocal(userId: string, settings: OsSettingsRequest): void {
  writeCachedOsSettings(userId, settings);
}

export interface UseOsUserSettingsResult {
  settings: OsSettingsRequest;
  isLoading: boolean;
  /** True once the initial GET /os-settings attempt has settled */
  isSettingsReady: boolean;
  reopenWindowsOnLaunch: boolean;
  openWindowsMaximized: boolean;
  setPinnedAppIds: (ids: string[]) => void;
  setHiddenAppIds: (ids: string[]) => void;
  setReopenWindowsOnLaunch: (enabled: boolean) => void;
  setOpenWindowsMaximized: (enabled: boolean) => void;
  pinApp: (appId: string) => void;
  unpinApp: (appId: string) => void;
  hideApp: (appId: string) => void;
  unhideApp: (appId: string) => void;
}

export function useOsUserSettings(): UseOsUserSettingsResult {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const userId = user?.id ?? null;

  const [settings, setSettings] = useState<OsSettingsRequest>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userIdRef = useRef<string | null>(userId);
  userIdRef.current = userId;

  // Initial load (local first, then server reconcile)
  useEffect(() => {
    if (authLoading) {
      setIsLoading(true);
      return;
    }
    if (!isAuthenticated || !userId) {
      setSettings(DEFAULT_SETTINGS);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const local = readLocal(userId);
    if (local) {
      setSettings(local);
    }

    getOsSettings()
      .then((server) => {
        if (cancelled || userIdRef.current !== userId) return;
        if (!server) return;
        const next: OsSettingsRequest = {
          pinnedAppIds: server.pinnedAppIds ?? [],
          hiddenAppIds: server.hiddenAppIds ?? [],
          reopenWindowsOnLaunch: server.reopenWindowsOnLaunch === true,
          openWindowsMaximized: server.openWindowsMaximized === true,
        };
        setSettings(next);
        writeLocal(userId, next);
      })
      .catch((err) => {
        console.error("[useOsUserSettings] failed to load:", err);
      })
      .finally(() => {
        if (!cancelled && userIdRef.current === userId) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, userId]);

  const persist = useCallback(
    (
      next: OsSettingsRequest,
      syncOptions?: { includeOpenWindowsMaximized?: boolean },
    ) => {
      const owner = userIdRef.current;
      if (!owner) return;
      setSettings(next);
      writeLocal(owner, next);

      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = setTimeout(() => {
        saveOsSettings(toOsSettingsSyncPayload(next, syncOptions)).catch((err) => {
          console.error("[useOsUserSettings] failed to sync:", err);
        });
      }, SYNC_DEBOUNCE_MS);
    },
    [],
  );

  useEffect(
    () => () => {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    },
    [],
  );

  const setPinnedAppIds = useCallback(
    (ids: string[]) => persist({ ...settings, pinnedAppIds: ids }),
    [persist, settings],
  );
  const setHiddenAppIds = useCallback(
    (ids: string[]) => persist({ ...settings, hiddenAppIds: ids }),
    [persist, settings],
  );
  const setReopenWindowsOnLaunch = useCallback(
    (enabled: boolean) =>
      persist({ ...settings, reopenWindowsOnLaunch: enabled }),
    [persist, settings],
  );
  const setOpenWindowsMaximized = useCallback(
    (enabled: boolean) =>
      persist(
        { ...settings, openWindowsMaximized: enabled },
        { includeOpenWindowsMaximized: true },
      ),
    [persist, settings],
  );

  const pinApp = useCallback(
    (appId: string) => {
      if (settings.pinnedAppIds.includes(appId)) return;
      persist({
        ...settings,
        pinnedAppIds: [...settings.pinnedAppIds, appId],
        hiddenAppIds: settings.hiddenAppIds.filter((id) => id !== appId),
      });
    },
    [persist, settings],
  );
  const unpinApp = useCallback(
    (appId: string) =>
      persist({
        ...settings,
        pinnedAppIds: settings.pinnedAppIds.filter((id) => id !== appId),
      }),
    [persist, settings],
  );
  const hideApp = useCallback(
    (appId: string) => {
      if (settings.hiddenAppIds.includes(appId)) return;
      persist({
        ...settings,
        hiddenAppIds: [...settings.hiddenAppIds, appId],
        pinnedAppIds: settings.pinnedAppIds.filter((id) => id !== appId),
      });
    },
    [persist, settings],
  );
  const unhideApp = useCallback(
    (appId: string) =>
      persist({
        ...settings,
        hiddenAppIds: settings.hiddenAppIds.filter((id) => id !== appId),
      }),
    [persist, settings],
  );

  return {
    settings,
    isLoading,
    isSettingsReady: !isLoading,
    reopenWindowsOnLaunch: settings.reopenWindowsOnLaunch === true,
    openWindowsMaximized: settings.openWindowsMaximized === true,
    setPinnedAppIds,
    setHiddenAppIds,
    setReopenWindowsOnLaunch,
    setOpenWindowsMaximized,
    pinApp,
    unpinApp,
    hideApp,
    unhideApp,
  };
}
